import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HeaderBar from "../../components/HeaderBar";
import LeftDrawer from "../../components/LeftDrawer";
import {
  Box,
  Toolbar,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, Refresh, Edit, Close, Cancel, Save, SaveAs, PersonAdd } from "@mui/icons-material";
import {
  getMembers,
  setTransactionId,
  resetSaveStatus,
  getMemberById,
} from "../../redux/masters/memberSlice";
import { TableSkeleton } from "../../components/common/SkeletonLoader";
import { useToast } from "../../components/common/ToastProvider";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EnhancedTable from "../../components/common/EnhancedTable";
import { getOptions } from "../../redux/masters/optionSlice";
import { OptionsStatus } from "../../constants/enum";
import { getOptionValueByText, getOptionText } from "../../utils/optionUtils";
import MemberForm from "../../components/modals/MemberForm";
import validateMemberForm from "../../utils/validator";
import { fetchMemberMasterData, uploadMemberFiles, fetchMemberByMemberNumber } from "../../services/masters/memberService";
import { saveMemberDetails } from "../../services/masters/memberService";

const MemberPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showSuccess, showError } = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [memberMasterData, setMemberMasterData] = useState({ members: [] });
  const [memberMasterLoading, setMemberMasterLoading] = useState(true);
  const [addLoading, setAddLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const { data, loading, error, saveStatus, transactionId, noRecordsFound } = useSelector(
    (state) => state.member || {}
  );

  const [formData, setFormData] = useState({
    id: null,
    memberName: "",
    memberCode: "",
    memberType: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    dateOfBirth: "",
    gender: "",
    active: true,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [memberFormData, setMemberFormData] = useState({});
  const [loadedMember, setLoadedMember] = useState(null);
  const [formResetToken, setFormResetToken] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const optionKey = OptionsStatus.MemberMaster;
  const {
    data: memberOptions = {},
    loading: optionLoading,
    error: optionError,
  } = useSelector((state) => state.option || {});
  const optionData = memberOptions[optionKey] || [];

  useEffect(() => {
    dispatch(getMembers());
    
    // Initialize member master data when page loads
    const initializeMemberMaster = async () => {
      try {
        setMemberMasterLoading(true);
        const response = await fetchMemberMasterData();
        dispatch(setTransactionId(response.data.data.transactionId));
        setMemberMasterData(response.data.data);
      } catch (error) {
        console.error('Error initializing member master data:', error);
        // Handle "No records found" as a normal state, not an error
        if (error.response?.data?.errorCode === 'NO_RECORDS_FOUND') {
          setMemberMasterData({ members: [] });
        }
        // Don't show error to user as this is initialization
      } finally {
        setMemberMasterLoading(false);
      }
    };
    
    initializeMemberMaster();
  }, [dispatch]);

  // Show a one-time info toast when no records found
  useEffect(() => {
    if (noRecordsFound) {
      showError("No members found");
    }
  }, [noRecordsFound, showError]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleAddNew = async () => {
    try {
      setAddLoading(true);
      await ensureMemberOptionsLoaded();
      setLoadedMember(null);
      setFormResetToken(Date.now());
      setOpenDialog(true);
    } catch (err) {
      showError("Failed to load member options");
    } finally {
      setAddLoading(false);
    }
  };

  const ensureMemberOptionsLoaded = async () => {
      if (!optionData || optionData.length === 0) {
        try {
          await dispatch(getOptions([optionKey])).unwrap();
        } catch (err) {
          showError("Failed to load options");
          throw err;
        }
      }
    };

  const handleRefresh = async () => {
    try {
      setRefreshLoading(true);
      await dispatch(getMembers());
      showSuccess("Data refreshed successfully");
    } catch (error) {
      showError("Failed to refresh data");
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleMemberSave = async (memberData, shouldResetForm = false) => {
    try {
      setSaveLoading(true);
      // Validate using centralized validators
      const { isValid, errors } = validateMemberForm(memberData);
      if (!isValid) {
        const firstKey = Object.keys(errors)[0];
        const firstError = errors[firstKey];
        const fieldLabels = {
          applicationId: "Application Id",
          applicationDate: "Application Date",
          memberCode: "Member Number",
          regDate: "Registration Date",
          nameTitle: "Title",
          name: "Full Name",
          gender: "Gender",
          age: "Age",
          email: "Email",
          mobile: "Mobile Number",
          maritalStatus: "Marital Status",
          marriageDate: "Marriage Date",
          fatherName: "Father Name",
          fatherTitle: "Father Title",
          soWoCo: "So/Wo/Co",
          dob: "Date of Birth",
          employeeId: "Employee Id",
          companyName: "Company Name",
          department: "Department",
          designation: "designation",
          headOfficeAddress: "Head Office Address",
          officePostingAddress: "Office Posting Address",
          telephoneNo: "Telephone",
          reportingManager: "Reporting Manager",
          income: "Income",
          pfGpfNo: "PF/GPF No",
          officeJoiningDate: "Office Joining Date",
          kycVerificationDate: "KYC Verification Date",
          electricityBill: "Electricity Bill",
          voterRationCardNo: "Voter/Ration Card No",
          passportDlNo: "Passport Number",
          bankAccountNo: "Bank Account No",
          bankName: "Bank Name",
          bankBranch: "Bank Branch",
          ifscCode: "IFSC Code",
          bankManager: "Bank Manager",
          bankAddress: "Bank Address",
          agentCode: "Agent Code",
          agentName: "Agent Name",
          leadDate: "Lead Date",
          nomineeName: "Nominee Name",
          nomineeTitle: "Nominee Title",
          nomineeRelation: "Nominee Relation",
          nomineeOccupation: "Nominee Occupation",
          nomineeMobileNo: "Nominee Mobile",
          nomineeDob: "Nominee DOB",
          nomineeAddress: "Nominee Address",
          nomineeAadhaarNo: "Nominee Aadhaar No",
          nomineePanNo: "Nominee Pan No",
          addresses: "Addresses",
          "addresses.0.addressType": "Address Type",
          "addresses.0.address": "Address",
          "addresses.0.pincode": "Pincode",
          "addresses.0.state": "State",
          "addresses.0.district": "District",
          "addresses.0.subDistrict": "Sub-District",
          "addresses.0.village": "Village",
          "addresses.0.contactNo": "Address Contact No",
        };
        const label = fieldLabels[firstKey] || firstKey;
        showError(`${label}: ${firstError || "Please fix validation errors"}`);
        return;
      }

      // Helper to resolve plain text from dynamic options (supports multiple schemas)
      const resolveOptionText = (groupNames, value) => {
        if (!value) return "";
        const groups = Array.isArray(groupNames) ? groupNames : [groupNames];
        // 1) Try within specified groups
        for (const groupName of groups) {
          const group = (optionData || []).find(
            (opt) => (opt.optionName || opt.name || opt.group) === groupName
          );
          if (group && Array.isArray(group.values)) {
            const matched = group.values.find(
              (item) => item?.optionValue === value || item?.value === value
            );
            if (matched) return matched.optionText || matched.text || "";
          }
        }
        // 2) Fallback: search across ALL option groups
        for (const group of (optionData || [])) {
          if (!Array.isArray(group.values)) continue;
          const matched = group.values.find(
            (item) => item?.optionValue === value || item?.value === value
          );
          if (matched) return matched.optionText || matched.text || "";
        }
        // 3) If value looks base64-encoded, try to decode
        const looksEncoded = /[=+/]|\d/.test(String(value)) && String(value).length > 10;
        if (looksEncoded) {
          try {
            const decoded = atob(String(value));
            // Accept short human-readable strings
            if (/^[\w\s.,\-()\/]+$/.test(decoded) && decoded.length <= 50) {
              return decoded;
            }
          } catch (_) {
            // ignore decode errors
          }
          return "";
        }
        return String(value);
      };

      // Convert selected option values to display texts expected by backend
      const nameTitleText = resolveOptionText([
        "Salutation",
      ], memberData.nameTitle || memberData.salutation);

      const genderText = resolveOptionText([
        "Gender",
        "GENDER",
        "GENDER_TYPE",
        "MEMBER_GENDER",
      ], memberData.gender);

      const maritalStatusText = resolveOptionText([
        "Marital Status",
        "MARITAL_STATUS",
        "MARITAL_STATUS_TYPE",
        "MEMBER_MARITAL_STATUS",
      ], memberData.maritalStatus);

      const religionText = resolveOptionText([
        "Religion",
        "RELIGION",
      ], memberData.religion);

      const fatherTitleText = resolveOptionText([
        "Father Salutation",
      ], memberData.fatherTitle);

      const soWoCoText = resolveOptionText([
        "SoWoCo",
      ], memberData.soWoCo);

      const occupationText = resolveOptionText([
        "Occupation",
        "OCCUPATION",
        "OCCUPATION_TYPE",
        "MEMBER_OCCUPATION",
      ], memberData.occupation || memberData.jobType);

      const nomineeRelationText = resolveOptionText([
        "Relation",
        "RELATION",
        "NOMINEE_RELATION",
        "Nominee Relation",
        "MEMBER_RELATION",
      ], memberData.nomineeRelation);

       // Build API payload according to backend contract
       const apiPayload = {
        id: memberData.id || memberData.memberId || null,
        fkSocietyBranchId: memberData.fkSocietyBranchId || null,
        applicationNo: memberData.applicationId || "",
         ledgerNo: memberData.ledgerNo || "",
        applicationDate: memberData.applicationDate || "",
        memberNo: memberData.memberCode || "",
        membershipDate: memberData.regDate || "",
        nameTitle: memberData.nameTitleText || nameTitleText || memberData.nameTitle || memberData.salutation || "",
        fullName: memberData.name || "",
        gender: memberData.genderText || genderText || memberData.gender || "",
        soWoCo: memberData.soWoCoText || soWoCoText || memberData.soWoCo || "",
        fatherTitle: memberData.fatherTitleText || fatherTitleText || memberData.fatherTitle || "",
        fatherName: memberData.fatherName || "",
        dob: memberData.dob || "",
        age: memberData.age ? Number(memberData.age) : null,
        emailId: memberData.email || "",
        mobileNo: memberData.mobile || "",
         alternateContactNo: memberData.alternateContactNo || "",
        religion: memberData.religionText || religionText || memberData.religion || "",
        // caste: memberData.caste || "",
        maritalStatus: maritalStatusText || memberData.maritalStatusText || memberData.maritalStatus || "",
        anniversaryDate: memberData.marriageDate || memberData.anniversaryDate || "",
        
        employeeId: memberData.employeeId || "",
        companyName: memberData.companyName || "",
         occupation: memberData.occupation || "",
        department: memberData.department || "",
         designation: memberData.designation || "",
        headOfficeAddress: memberData.headOfficeAddress || "",
        officePostingAddress: memberData.officePostingAddress || "",
        telephoneNo: memberData.telephoneNo || "",
        reportingManager: memberData.reportingManager || "",
        income: memberData.income || null,
        pfGpfNo: memberData.pfGpfNo || "",
        officeJoiningDate: memberData.officeJoiningDate || "",

        aadhaarNo: memberData.aadharNumber || "",
        panNo: memberData.panNumber || "",
        passportDlNo: memberData.passportDlNo || "",
        electricityBill: memberData.electricityBill || "",
        voterRationCardNo: memberData.voterRationCardNo || "",
        kycVerificationDate: memberData.kycVerificationDate || "",
        bankAccountNo: memberData.bankAccountNo || "",
        bankName: memberData.bankName || "",
        bankBranch: memberData.bankBranch || "",
        ifscCode: memberData.ifscCode || "",
        bankManager: memberData.bankManager || "",
        bankAddress: memberData.bankAddress || "",
         fkSocietyAgentId: memberData.fkSocietyAgentId || (memberData.agentCode ? Number(memberData.agentCode) : null),
        agentName: memberData.agentName || "",
        leadDate: memberData.leadDate || "",

        nomineeName: memberData.nomineeName || "",
        nomineeRelation: memberData.nomineeRelationText || nomineeRelationText || memberData.nomineeRelation || "",
        nomineeOccupation: memberData.nomineeOccupation || "",
        nomineeMobileNo: memberData.nomineeMobileNo || "",
        nomineeDob: memberData.nomineeDob || "",
        nomineeAddressSame: Boolean(memberData.nomineeAddressSame) || false,
        nomineeAddress: memberData.nomineeAddress || "",
         nomineeAadhaarNo: memberData.nomineeAadhaarNo || "",
         nomineePanNo: memberData.nomineePanNo || "",
        membershipStatus: memberData.membershipStatus || "Active",
        meetingDate: memberData.meetingDate || "",
        resignationDate: memberData.resignationDate || "",
        // resignNo: memberData.resignNo || "",
        // resignAcceptanceDate: memberData.resignAcceptanceDate || "",
        // resignStatus: memberData.resignStatus || "",
        // updatedAt: memberData.updatedAt || null,
        // active: memberData.active !== undefined ? Boolean(memberData.active) : true,

        addresses: (() => {
          return (memberData.addresses || []).length > 0 ? (memberData.addresses || []).map((addr) => {
            const addressTypeText = (addr.addressTypeText && String(addr.addressTypeText).trim() !== "")
              ? addr.addressTypeText
              : resolveOptionText(["Address Type"], addr.addressType);
            return {
              id: addr.id || null,
              fkMemberId: memberData.memberId || null,
              addressType: addressTypeText || "",
              address: addr.address || "",
              pinCode: addr.pinCode || addr.pincode || "",
              stateName: addr.stateName || addr.state || "",
              districtName: addr.districtName || addr.district || "",
              subDistrictName: addr.subDistrictName || addr.subDistrict || "",
              villageName: addr.villageName || addr.village || "",
              stateCode: addr.stateCode || "",
              districtCode: addr.districtCode || "",
              subDistrictCode: addr.subDistrictCode || "",
              villageCode: addr.villageCode || "",
              contactNo: addr.contactNo || memberData.mobile || "",
              ownership: addr.houseStatus || addr.ownership || "",
              careOf: addr.careOfName || addr.careOf || "",
              active: true,
              defaultAddress: addr.defaultAddress || false,
            };
          }) : [];
        })()
       };



       // Save/Update member using API (same endpoint, backend handles create vs update based on ID presence)
       const saveResponse = await saveMemberDetails(apiPayload);
       console.log(apiPayload)
       if (saveResponse?.data?.statuscode === 200) {
         const actionText = apiPayload.id ? "updated" : "saved";
         showSuccess(`Member data ${actionText} successfully`);
         
         // Reset form if "Save & New" was clicked
         if (shouldResetForm) {
           setFormResetToken(Date.now());
           setLoadedMember(null);
           setEditingMemberId(null);
         }
       } else {
         console.error("Save response:", saveResponse?.data);
         showError(saveResponse?.data?.message || "Failed to save member");
         return;
       }

      // Note: Photograph and signature files are now uploaded immediately when selected
      // No need to upload them again during save

      // success already shown above
      
     } catch (error) {
       console.error("Error saving member data:", error);
       console.error("Error response data:", error.response?.data);
       console.error("Error response status:", error.response?.status);
       showError("Error saving member data. Please try again.");
     } finally {
       setSaveLoading(false);
     }
  };

  const handleFormDataChange = (formData) => {
    setMemberFormData(formData);
  };

  useEffect(() => {
    if (saveStatus === "success") {
      setFormData({
        id: null,
        memberName: "",
        memberCode: "",
        memberType: "",
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        dateOfBirth: "",
        gender: "",
        active: true,
      });
      dispatch(resetSaveStatus());
    }
  }, [saveStatus, dispatch]);

  const handleEditClick = (member) => {
    const memberId = member?.id ?? member?.memberId ?? member?.memberNo;
    if (!memberId) {
      showError("Member id not found for this row");
      return;
    }
    setEditingMemberId(memberId);
    if (!optionData || optionData.length === 0) {
      dispatch(getOptions([OptionsStatus.MemberMaster])).then(() => {
        fetchAndSetMember(member);
      });
    } else {
      fetchAndSetMember(member);
    }
  };

  const fetchAndSetMember = async (row) => {
    try {
      // Fetch by member number - this API returns all member data including documents
      if (row?.memberNo) {
        const res = await fetchMemberByMemberNumber(row.memberNo);
        if (res?.data?.statuscode === 200 && res?.data?.data) {
          setLoadedMember(res.data.data);
          // console.log('Loaded member:', res.data.data);
          setFormResetToken(null);
          setOpenDialog(true);
          setEditingMemberId(null);
          return;
        }
        console.warn('Fetch by memberNo failed:', res?.data);
        showError("Failed to load member details");
        setEditingMemberId(null);
      } else {
        showError("Member number not found for this row");
        setEditingMemberId(null);
      }
    } catch (err) {
      console.error('Error fetching member details:', err);
      showError('Unable to load member. Please try again.');
      setEditingMemberId(null);
    }
  };

  useEffect(() => {
  }, [formData]);

  return (
    <Box sx={{ display: "flex" }}>
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/masters/member"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: "100vh",
          width: { sm: `calc(100% - ${isMobile ? 0 : 240}px)` },
        }}
      >
        <Toolbar sx={{ minHeight: 64 }} />

        <Breadcrumbs />

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardHeader
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: "background.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            title={
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Member Master
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    disabled={refreshLoading}
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    {refreshLoading ? <CircularProgress size={16} /> : <Refresh />}
                  </IconButton>
                </Tooltip>

                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleAddNew}
                  startIcon={
                    addLoading ? <CircularProgress size={16} /> : <Add />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                  disabled={addLoading}
                >
                  {isMobile ? "Add" : "Add New"}
                </Button>

                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleRefresh}
                    startIcon={refreshLoading ? <CircularProgress size={16} /> : <Refresh />}
                    disabled={refreshLoading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {memberMasterLoading ? (
              <TableSkeleton rows={5} columns={5} />
            ) : error ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  "& .MuiAlert-message": {
                    fontSize: "0.875rem",
                  },
                }}
              >
                {typeof error === 'object' ? JSON.stringify(error) : error}
              </Alert>
            ) : noRecordsFound ? (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  "& .MuiAlert-message": {
                    fontSize: "0.875rem",
                  },
                }}
              >
                No members found
              </Alert>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    },
                  },
                }}
              >
                <EnhancedTable
                  data={memberMasterData.members || []}
                  columns={[
                  {
                    id: "memberNo",
                    label: "M.No",
                    sortable: true,
                    minWidth: 60,
                    render: (value) => value,
                  },
                  {
                    id: "membershipDate",
                    label: "M.Date",
                    sortable: true,
                    minWidth: 90,
                    render: (value) => value,
                  },
                  {
                    id: "fullName",
                    label: "Name",
                    sortable: true,
                    minWidth: 180,
                    render: (value, row) => `${row.nameTitle} ${value}`,
                  },
                  {
                    id: "fatherName",
                    label: "Father Name",
                    sortable: true,
                    minWidth: 200,
                    render: (value, row) => `${row.soWoCo ? row.soWoCo : ''} ${row.fatherTitle ? row.fatherTitle : ''} ${value ? value : ''}`,
                  },
                  {
                    id: "mobileNo",
                    label: "Mobile No",
                    sortable: true,
                    minWidth: 130,
                    render: (value) => value,
                  },
                  {
                    id: "membershipStatus",
                    label: "Status",
                    sortable: true,
                    minWidth: 100,
                    render: (value) => (
                      <Box sx={{ px: 1 }}>
                        {value === "Active" ? (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            variant="filled"
                          />
                        ) : (
                          <Chip
                            label={value || "Inactive"}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    ),
                  },
                  {
                    id: "updatedAt",
                    label: "Updated At",
                    sortable: true,
                    minWidth: 150,
                    render: (value) => value,
                  },
                  {
                    id: "editable",
                    label: "Edit",
                    sortable: false,
                    minWidth: 80,
                  render: (_, row) => {
                    const memberId = row?.id ?? row?.memberId ?? row?.memberNo;
                    const isLoading = editingMemberId === memberId;
                    return (
                      <Box sx={{ px: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tooltip
                          title={'Edit Member'}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(row)}
                              disabled={isLoading}
                            >
                              {isLoading ? <CircularProgress size={16} /> : <Edit />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    );
                  },
                  },
                ]}
                title="Members"
                loading={loading}
                emptyMessage="No members found"
                stickyHeader={true}
                dense={true}
                showRowNumbers={true}
              />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          // Only allow closing via escape key, not backdrop click
          if (reason === 'escapeKeyDown') {
            setShowCancelConfirmation(true);
          }
        }}
        fullScreen={{ xs: true, md: false }} // responsive fullscreen on mobile
        PaperProps={{
          sx: {
            height: "auto",
            maxHeight: "98vh", // limit max height
            width: {
              xs: "600px", // full width on small screens
              sm: "95vw", // 95% on tablet
              md: "90vw",
              lg: "90vw",
              xl: "90vw", // cap at desktop
            },
            margin: 0, // remove default margin
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between", // Keep existing
            alignItems: "center",
            pb: 1,
            px: { xs: 1, sm: 2 },
            pt: { xs: 1, sm: 2 },
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.primary.dark
                : theme.palette.primary.main,
            color: "white",
            borderRadius: isMobile ? 0 : "16px 16px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonAdd />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formData.id ? "Edit Member" : "Add New Member"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowCancelConfirmation(true)}
            size="small"
            sx={{
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          <Box
            sx={{
              mb: 3,
              width: "50%",
              justifyContent: "center",
              alignItems: "center",
              mx: "auto",
              my: 2.5,
            }}
          >
          </Box>
          <MemberForm
            onFormDataChange={handleFormDataChange}
            loadedMember={loadedMember}
            resetToken={formResetToken}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            gap: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(30, 41, 59, 0.2)"
                : "rgba(248, 250, 252, 0.8)",
            borderRadius: isMobile ? 0 : "0 0 16px 16px",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                onClick={() => handleMemberSave(memberFormData)}
                variant="contained"
                startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveAs />}
                disabled={saveLoading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  backgroundColor: "#ff8c00",
                  color: "#fff",
                  boxShadow: "0 4px 15px rgba(255, 140, 0, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#e67e00",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(255, 140, 0, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Membership Form
              </Button>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <Button
                onClick={() => setShowCancelConfirmation(true)}
                variant="outlined"
                startIcon={<Cancel />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  border: "2px solid",
                  borderColor: "#ff4757",
                  color: "#ff4757",
                  backgroundColor: "transparent",
                  boxShadow: "0 2px 8px rgba(255,71,87,0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "#ff3742",
                    backgroundColor: "#ff4757",
                    color: "#fff",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 15px rgba(255,71,87,0.3)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await handleMemberSave(memberFormData);
                }}
                variant="contained"
                startIcon={saveLoading ? <CircularProgress size={16} /> : <Save />}
                disabled={saveLoading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  backgroundColor: "#2ed573",
                  color: "#fff",
                  boxShadow: "0 4px 15px rgba(46, 213, 115, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#26c952",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(46, 213, 115, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Save
              </Button>
              <Button
                onClick={() => handleMemberSave(memberFormData, true)}
                variant="contained"
                startIcon={saveLoading ? <CircularProgress size={16} /> : <Add />}
                disabled={saveLoading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  backgroundColor: "#667eea",
                  color: "#fff",
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#5a6fd8",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Save & New
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.palette.warning.main }}>
          Confirm Cancel
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? Any unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCancelConfirmation(false)}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            No, Keep Editing
          </Button>
          <Button
            onClick={() => {
              setShowCancelConfirmation(false);
              setOpenDialog(false);
            }}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberPage;
